import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Utensils, Pizza, Wine, Coffee, GlassWater, ChefHat } from "lucide-react";
import { Button } from "../components/ui/button";
import { RESTAURANT, IMAGES } from "../lib/restaurant";
import { SIGNATURE } from "../lib/menuData";
import { useLang } from "../i18n/LanguageContext";

const SIG_ICONS = {
  pizza: Pizza, antipasti: Utensils, pasta: Utensils, main: ChefHat, dessert: Coffee, drinks: Wine,
};
const SIG_IMG = {
  pizza: "https://customer-assets.emergentagent.com/job_segreto-dining/artifacts/5tj25cp2_tg_image_1581353394.jpeg",
  antipasti: "https://customer-assets.emergentagent.com/job_segreto-dining/artifacts/zmt8lf39_tg_image_2235018006.jpeg",
  pasta: "https://customer-assets.emergentagent.com/job_segreto-dining/artifacts/nbgm0mhh_tg_image_2038597416.jpeg",
  main: IMAGES.interiorBar, dessert: IMAGES.tiramisu,
  drinks: "https://customer-assets.emergentagent.com/job_segreto-dining/artifacts/a6ew9k1g_tg_image_1654659884.jpeg",
};

export default function Home() {
  const { t } = useLang();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.heroEvening} alt="Segreto interior at evening" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/85" />
        </div>
        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-5 sm:px-8 lg:px-12 py-32 text-cream">
          <p className="uppercase-track text-xs text-gold mb-6 animate-fade-up">{t.home.heroEyebrow}</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[1.05] mb-6 max-w-4xl animate-fade-up delay-100">
            {t.home.heroTitle1}<br />
            <span className="italic text-gold">{t.home.heroTitle2}</span> {t.home.heroTitle2b}
          </h1>
          <p className="font-serif italic text-2xl sm:text-3xl text-cream/85 mb-6 animate-fade-up delay-200">{t.home.heroSubtitle}</p>
          <p className="max-w-xl text-base sm:text-lg text-cream/75 mb-10 leading-relaxed animate-fade-up delay-300">{t.home.heroText}</p>
          <div className="flex flex-wrap gap-4 animate-fade-up delay-400">
            <a href="tel:+48518805767">
              <Button data-testid="hero-reserve" className="rounded-none bg-wine hover:bg-wine/85 text-cream uppercase-track text-xs h-14 px-8">{t.common.callToReserve}</Button>
            </a>
            <Link to="/menu">
              <Button data-testid="hero-menu" variant="outline" className="rounded-none bg-transparent border-cream text-cream hover:bg-cream hover:text-charcoal uppercase-track text-xs h-14 px-8">{t.common.viewMenu}</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO with background */}
      <section
        className="relative py-24 sm:py-32 bg-charcoal bg-cover bg-center"
        style={{ backgroundImage: `url("https://customer-assets.emergentagent.com/job_segreto-dining/artifacts/bzhyzoq3_ChatGPT%20Image%207%20%D0%B8%D1%8E%D0%BD.%202026%20%D0%B3.%2C%2017_19_20.png")` }}
        data-testid="intro-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/75 to-charcoal/35" />
        <div className="absolute inset-0 bg-charcoal/30" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-start text-cream">
          <div className="lg:col-span-5">
            <p className="uppercase-track text-xs text-gold mb-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{t.home.introEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">
              {t.home.introTitle}<br />{t.home.introTitle2}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6 space-y-6 text-cream/90 text-lg leading-relaxed drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]">
            <p>{t.home.introP1}</p>
            <p>{t.home.introP2}</p>
            <Link to="/o-nas" className="inline-flex items-center gap-2 text-gold font-medium uppercase-track text-xs hover:gap-3 transition-all">
              {t.common.learnMore} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SIGNATURE CATEGORIES */}
      <section className="bg-charcoal py-24 sm:py-32 text-cream" data-testid="signature-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="uppercase-track text-xs text-gold mb-5">{t.home.sigEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-5">{t.home.sigTitle}</h2>
            <p className="text-cream/70 text-lg leading-relaxed">{t.home.sigIntro}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/10">
            {SIGNATURE.map((key, idx) => {
              const Icon = SIG_ICONS[key] || GlassWater;
              const card = t.sigCards[idx];
              return (
                <div key={key} data-testid={`signature-card-${idx}`} className="group relative bg-charcoal p-8 sm:p-10 overflow-hidden transition-all hover:bg-wine/15">
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-20 group-hover:opacity-40 transition-opacity">
                    <img src={SIG_IMG[key]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <Icon className="h-7 w-7 text-gold mb-6" />
                  <h3 className="font-serif text-2xl sm:text-3xl mb-3">{card.title}</h3>
                  <p className="text-cream/65 text-sm leading-relaxed">{card.text}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-14">
            <Link to="/menu">
              <Button data-testid="signature-full-menu" className="rounded-none bg-cream text-charcoal hover:bg-gold uppercase-track text-xs h-12 px-8">{t.common.viewFullMenu}</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT WITH PHOTO */}
      <section className="bg-cream py-24 sm:py-32" data-testid="about-preview-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <p className="uppercase-track text-xs text-wine mb-5">{t.home.aboutEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-8 leading-tight">
              {t.home.aboutTitle}<br />{t.home.aboutTitle2}
            </h2>
            <div className="space-y-5 text-charcoal/75 leading-relaxed">
              <p>{t.home.aboutP1}</p>
              <p>{t.home.aboutP2}</p>
            </div>
            <Link to="/o-nas" className="mt-8 inline-flex items-center gap-2 text-wine uppercase-track text-xs font-medium hover:gap-3 transition-all">
              {t.common.moreAboutUs} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="order-1 lg:order-2 relative">
            <img src={IMAGES.muralDay} alt="Segreto interior" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-charcoal text-cream p-6 max-w-[220px]">
              <p className="font-serif italic text-xl leading-tight">{t.home.aboutQuote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* MENU PREVIEW */}
      <section className="bg-charcoal py-20 sm:py-28 text-cream" data-testid="menu-preview-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="uppercase-track text-xs text-gold mb-5">{t.home.menuEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight">{t.home.menuTitle}<br />{t.home.menuTitle2}</h2>
            <p className="text-cream/70 text-lg leading-relaxed max-w-xl mb-8">{t.home.menuText}</p>
            <Link to="/menu">
              <Button data-testid="menu-preview-cta" className="rounded-none bg-wine hover:bg-wine/85 text-cream uppercase-track text-xs h-12 px-8">{t.common.goToMenu}</Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.pasta} alt="Pasta" className="w-full aspect-square object-cover" />
            <img src={IMAGES.tiramisu} alt="Tiramisu" className="w-full aspect-square object-cover mt-10" />
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="bg-cream py-24 sm:py-32" data-testid="gallery-preview-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <p className="uppercase-track text-xs text-wine mb-5">{t.home.galleryEyebrow}</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight">{t.home.galleryTitle}</h2>
            </div>
            <p className="md:max-w-md text-charcoal/70 leading-relaxed">{t.home.galleryText}</p>
          </div>
          <div className="grid grid-cols-12 gap-3 sm:gap-4">
            <img src={IMAGES.muralDay} alt="Mural" className="col-span-12 md:col-span-7 aspect-[16/10] object-cover" />
            <img src={IMAGES.heroEvening} alt="Evening" className="col-span-6 md:col-span-5 aspect-[4/5] object-cover" />
            <img src={IMAGES.pasta} alt="Pasta" className="col-span-6 md:col-span-3 aspect-square object-cover" />
            <img src={IMAGES.aperi} alt="Aperitivo" className="col-span-6 md:col-span-3 aspect-square object-cover" />
            <div className="col-span-12 md:col-span-6 aspect-square sm:aspect-auto bg-charcoal text-cream flex flex-col justify-end p-8">
              <h3 className="font-serif text-3xl mb-3">{t.home.galleryCard}</h3>
              <p className="text-cream/70 text-sm mb-6">{t.home.galleryCardText}</p>
              <Link to="/galeria">
                <Button data-testid="gallery-cta" className="rounded-none bg-cream text-charcoal hover:bg-gold uppercase-track text-xs h-11 px-6 w-fit">{t.common.viewGallery}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RESERVATION CTA (phone only) */}
      <section className="relative py-28 sm:py-36 overflow-hidden" data-testid="reservation-cta-section">
        <div className="absolute inset-0">
          <img src={IMAGES.heroEvening} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal/85" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 text-center text-cream">
          <p className="uppercase-track text-xs text-gold mb-6">{t.home.reserveEyebrow}</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl mb-6">{t.home.reserveTitle}</h2>
          <p className="max-w-xl mx-auto text-cream/75 mb-10 leading-relaxed text-lg">{t.home.reserveText}</p>
          <a href={`tel:${RESTAURANT.phoneRaw}`} data-testid="home-call-now" className="inline-flex items-center gap-3 font-serif text-3xl sm:text-5xl text-gold hover:text-cream transition mb-10">
            <Phone className="h-7 w-7" /> {RESTAURANT.phone}
          </a>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${RESTAURANT.phoneRaw}`}>
              <Button data-testid="home-call-button" className="rounded-none bg-wine hover:bg-wine/85 text-cream uppercase-track text-xs h-12 px-8">{t.common.callNow}</Button>
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT PREVIEW */}
      <section className="bg-cream py-24" data-testid="contact-preview-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="uppercase-track text-xs text-wine mb-5">{t.home.contactEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-6 leading-tight">{t.home.contactTitle}<br />{t.home.contactTitle2}</h2>
            <p className="text-charcoal/75 leading-relaxed mb-8 text-lg max-w-xl">{t.home.contactText}</p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3 text-charcoal">
                <MapPin className="h-5 w-5 text-wine mt-1" />
                <p>{RESTAURANT.address.street}<br />{RESTAURANT.address.postal}</p>
              </div>
              <div className="flex items-center gap-3 text-charcoal">
                <Phone className="h-5 w-5 text-wine" />
                <a href={`tel:${RESTAURANT.phoneRaw}`} className="hover:text-wine">{RESTAURANT.phone}</a>
              </div>
            </div>
            <Link to="/kontakt">
              <Button data-testid="home-contact-cta" className="rounded-none bg-charcoal text-cream hover:bg-wine uppercase-track text-xs h-12 px-8">{t.common.getThere}</Button>
            </Link>
          </div>
          <div className="aspect-[4/3] overflow-hidden border border-charcoal/10">
            <iframe title="Segreto map" src={RESTAURANT.mapsEmbed} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>

      {/* SISTER RESTAURANT */}
      <section className="bg-cream py-16 border-t border-charcoal/10" data-testid="sister-section">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="bg-[hsl(40_38%_93%)] border border-charcoal/10 shadow-[0_2px_20px_rgba(0,0,0,0.04)] rounded-sm overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0 items-stretch">
              <div className="p-10 sm:p-12 flex flex-col justify-center">
                <p className="uppercase-track text-[10px] text-wine mb-3">{t.home.sisterEyebrow}</p>
                <h3 className="font-serif text-2xl sm:text-3xl text-charcoal mb-4 leading-tight">
                  {t.home.sisterTitle}<br />
                  <span className="italic text-wine text-xl">{t.home.sisterTitle2}</span>
                </h3>
                <p className="text-charcoal/75 text-sm leading-relaxed mb-5">{t.home.sisterText}</p>
                <div className="text-charcoal/80 text-sm mb-7 leading-relaxed">
                  <p className="text-charcoal font-medium">Pizzeria Calabria</p>
                  <p>ul. Obrońców Westerplatte 19<br />78-104 Kołobrzeg</p>
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Pizzeria+Calabria+Obro%C5%84c%C3%B3w+Westerplatte+19+Ko%C5%82obrzeg"
                  target="_blank" rel="noopener noreferrer" data-testid="sister-maps-link"
                  className="inline-flex items-center gap-2 self-start border border-charcoal/40 text-charcoal hover:bg-charcoal hover:text-cream uppercase-track text-[11px] px-5 py-3 transition"
                >
                  {t.common.seeLocation}
                </a>
              </div>
              <div className="p-10 sm:p-12 flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_segreto-dining/artifacts/vlr85jsr_ChatGPT%20Image%207%20%D0%B8%D1%8E%D0%BD.%202026%20%D0%B3.%2C%2018_14_33.png"
                  alt="Pizzeria Calabria Kołobrzeg"
                  className="max-h-40 sm:max-h-48 w-auto object-contain mix-blend-multiply"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
